/**
 * Peril value formatting and risk colouring.
 *
 * Formatting matches the legacy K2 grid exactly, because underwriters read the
 * two side by side during parity checks: thousands separators on the ARI GL
 * columns, and the integer Bushfire Attack Level code rendered to two decimal
 * places, so code 1 reads "1.00".
 */

import { FLOOD_SCORE_MAX, FLOOD_SCORE_MIN } from '../api/types'

const WHOLE = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 })
/** Elevation is metres above the Australian Height Datum. */
const METRES = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1 })

/**
 * A step on the six part risk ramp, or "unknown" for a Bushfire Attack Level
 * recorded as -1. Maps to the --risk-* tokens.
 */
export type RiskStep = 1 | 2 | 3 | 4 | 5 | 6 | 'unknown'

/** Blank values stay blank. Never render the word null. */
export function formatText(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return ''
  return String(value)
}

/** Yes or No, as the legacy grid's Primary Ref column shows it. */
export function formatYesNo(value: boolean | null | undefined): string {
  if (value === null || value === undefined) return ''
  return value ? 'Yes' : 'No'
}

/** Whole years with a thousands separator, so the ceiling reads "10,001". */
export function formatAri(value: number | null | undefined): string {
  if (value === null || value === undefined) return ''
  return WHOLE.format(value)
}

export function formatElevation(value: number | null | undefined): string {
  if (value === null || value === undefined) return ''
  return METRES.format(value)
}

/**
 * Two decimal places, matching the legacy grid: `0.00`, `-1.00`, `68.80`.
 *
 * The value is decimal(5,1) in PDP, not an integer, so this is rendering a real
 * fraction rather than padding a code. -1.00 is the Unknown sentinel.
 */
export function formatBushfireLevel(level: number | null | undefined): string {
  if (level === null || level === undefined) return ''
  return level.toFixed(2)
}

/** 1 is least risk, 6 is riskiest, so the score is already the ramp step. */
export function floodScoreRisk(score: number | null | undefined): RiskStep | null {
  if (score === null || score === undefined) return null
  if (score < FLOOD_SCORE_MIN || score > FLOOD_SCORE_MAX) return null
  return score as RiskStep
}

/**
 * Severity comes from the **category**, not from the Bushfire Attack Level.
 *
 * This used to shift the numeric level onto the ramp, on the understanding that
 * it was a category code running 0 to 5. It is not: it is a decimal measure, and
 * production returned 68.80 alongside the category "Medium". Anything above 5
 * therefore fell off the end of the ramp and lost its colour, while the legacy
 * grid fills it orange.
 *
 * The category names are ordered, so they map onto the six step ramp directly.
 * Unknown is a recorded value rather than missing data, so it takes the neutral
 * fill instead of a severity colour.
 */
const BUSHFIRE_CATEGORY_RISK: Record<string, RiskStep> = {
  negligible: 1,
  'very low': 2,
  low: 3,
  medium: 4,
  high: 5,
  'very high': 6,
}

export function bushfireRisk(category: string | null | undefined): RiskStep | null {
  if (!category) return null
  const key = category.trim().toLowerCase()
  if (key === 'unknown') return 'unknown'
  return BUSHFIRE_CATEGORY_RISK[key] ?? null
}
